import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { functions } from '../../config/firebase';
import { buildOpenResponseLinkState } from './adminProspectResponseNotifications.js';
import { shouldApplyPushOperationResult } from '../../services/admin/prospectWebPushClient.js';
import { resolveAdminProspectPushRef } from '../../services/admin/prospectWebPushService.js';

/**
 * HashRouter-safe opaque push ref resolver.
 * Does not auto-process the public response; only navigates to Prospect 360 focus state.
 * Session/ref-owned: waits for auth readiness, restarts after effect cleanup, supports retry.
 */
export default function AdminProspectPushOpenPage() {
  const { t } = useLanguage();
  const { currentUser, adminRole, adminRoleResolved, impersonation, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorKey, setErrorKey] = useState(null);
  const [status, setStatus] = useState('loading');
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const pushRef = searchParams.get('ref');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resolveOnce = useCallback(async () => {
    const generation = (generationRef.current += 1);
    const requestAuthUid = currentUser?.uid ? String(currentUser.uid) : '';
    const requestRef = typeof pushRef === 'string' ? pushRef : '';
    setErrorKey(null);
    setStatus('loading');

    if (loading || !adminRoleResolved) {
      setStatus('waiting_auth');
      return;
    }
    if (impersonation?.active) {
      if (
        shouldApplyPushOperationResult({
          mounted: mountedRef.current,
          requestGeneration: generation,
          activeGeneration: generationRef.current,
          requestAuthUid,
          activeAuthUid: currentUser?.uid ? String(currentUser.uid) : '',
        })
      ) {
        setErrorKey('admin.notifications.push.impersonationBlocked');
        setStatus('error');
      }
      return;
    }
    if (adminRole !== 'super_admin' || !currentUser?.uid) {
      if (
        shouldApplyPushOperationResult({
          mounted: mountedRef.current,
          requestGeneration: generation,
          activeGeneration: generationRef.current,
          requestAuthUid,
          activeAuthUid: currentUser?.uid ? String(currentUser.uid) : '',
        })
      ) {
        setErrorKey('admin.notifications.push.resolveDenied');
        setStatus('error');
      }
      return;
    }

    try {
      const resolved = await resolveAdminProspectPushRef(
        { ref: requestRef },
        { httpsCallable, functions },
      );
      if (
        !shouldApplyPushOperationResult({
          mounted: mountedRef.current,
          requestGeneration: generation,
          activeGeneration: generationRef.current,
          requestAuthUid,
          activeAuthUid: currentUser?.uid ? String(currentUser.uid) : '',
        })
      ) {
        return;
      }
      navigate(`/admin/prospection/${resolved.prospectId}`, {
        replace: true,
        state: buildOpenResponseLinkState(resolved),
      });
    } catch {
      if (
        shouldApplyPushOperationResult({
          mounted: mountedRef.current,
          requestGeneration: generation,
          activeGeneration: generationRef.current,
          requestAuthUid,
          activeAuthUid: currentUser?.uid ? String(currentUser.uid) : '',
        })
      ) {
        setErrorKey('admin.notifications.push.resolveError');
        setStatus('error');
      }
    }
  }, [
    adminRole,
    adminRoleResolved,
    currentUser?.uid,
    impersonation?.active,
    loading,
    navigate,
    pushRef,
  ]);

  useEffect(() => {
    void resolveOnce();
    return () => {
      // Invalidate in-flight work on cleanup / restart — do not use a permanent started guard.
      generationRef.current += 1;
    };
  }, [resolveOnce]);

  return (
    <div className="mx-auto max-w-lg px-4 py-10">
      <h1 className="text-lg font-semibold text-slate-900">{t('admin.notifications.push.resolveTitle')}</h1>
      {errorKey ? (
        <div className="mt-3" role="alert">
          <p className="text-sm text-rose-700">{t(errorKey)}</p>
          {status === 'error' && errorKey === 'admin.notifications.push.resolveError' ? (
            <button
              type="button"
              className="mt-2 text-sm font-medium text-indigo-700 underline-offset-2 hover:underline"
              onClick={() => void resolveOnce()}
            >
              {t('admin.notifications.push.resolveRetry')}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-600">{t('admin.notifications.push.resolveLoading')}</p>
      )}
    </div>
  );
}
